import { create } from "zustand";
import type { Breadcrumb, Filters, ViewObject } from "../types/reports";

// Labels conhecidas pra cada view — usadas no breadcrumb quando o caller
// não passa um label explícito.
const VIEW_LABELS: Record<string, string> = {
    home: "Dashboard",
    flows: "Flows",
    flow: "Flow",
    broadcasts: "Broadcasts",
    broadcast: "Broadcast",
    esteira: "Esteira",
    esteira_group: "Esteira",
    cliques: "Cliques",
    paises: "Países",
};

interface DashboardState {
    view: ViewObject;
    breadcrumbs: Breadcrumb[];
    openNode: { flowId: string; nodeId: string } | null;
    filters: Filters;
    toggles: {
        cliquesView: "torre" | "bars";
        horaView: "bars" | "heat";
        esteiraCompare: boolean;
    };

    // actions
    setView: (
        next: ViewObject,
        label?: string,
        opts?: { keepParent?: boolean }
    ) => void;
    goToCrumb: (index: number) => void;
    goHome: () => void;
    setOpenNode: (n: { flowId: string; nodeId: string } | null) => void;
    setFilter: <K extends keyof Filters>(k: K, v: Filters[K]) => void;
    resetFilters: () => void;
    setToggle: <K extends keyof DashboardState["toggles"]>(
        k: K,
        v: DashboardState["toggles"][K]
    ) => void;
}

const DEFAULT_FILTERS: Filters = {
    period: "yesterday",
    from: null,
    to: null,
    project: "all",
    category: "all",
    compare: false,
};

export const useDashboardStore = create<DashboardState>((set, get) => ({
    view: { name: "home" },
    breadcrumbs: [{ label: "Dashboard", view: { name: "home" } }],
    openNode: null,
    filters: DEFAULT_FILTERS,
    toggles: {
        cliquesView: "torre",
        horaView: "bars",
        esteiraCompare: false,
    },

    setView: (next, label, opts = {}) => {
        const leafLabel = label || VIEW_LABELS[next.name] || next.name;
        const leaf: Breadcrumb = { label: leafLabel, view: next };

        if (opts.keepParent) {
            // Drill-down: anexa ao trail (preserva grouped broadcast acima)
            const prev = get().breadcrumbs;
            const last = prev[prev.length - 1];
            // evita duplicar se reclicar mesma view
            if (
                last &&
                last.view?.name === next.name &&
                last.view?.id === next.id &&
                last.view?.contentHash === next.contentHash
            ) {
                set({ view: next });
                return;
            }
            set({ view: next, breadcrumbs: [...prev, leaf] });
            return;
        }

        let newCrumbs: Breadcrumb[];
        if (next.name === "home") {
            newCrumbs = [{ label: "Dashboard", view: { name: "home" } }];
        } else if (["flow", "broadcast", "esteira_group"].includes(next.name)) {
            const parent =
                next.name === "flow"
                    ? "flows"
                    : next.name === "broadcast"
                      ? "broadcasts"
                      : "esteira";
            newCrumbs = [
                { label: "Dashboard", view: { name: "home" } },
                {
                    label: VIEW_LABELS[parent],
                    view: { name: parent as ViewObject["name"] },
                },
                leaf,
            ];
        } else {
            newCrumbs = [
                { label: "Dashboard", view: { name: "home" } },
                leaf,
            ];
        }
        set({ view: next, breadcrumbs: newCrumbs });
    },

    goToCrumb: (index) => {
        const { breadcrumbs } = get();
        const crumb = breadcrumbs[index];
        if (!crumb) return;
        set({
            view: crumb.view,
            breadcrumbs: breadcrumbs.slice(0, index + 1),
        });
    },

    goHome: () => {
        set({
            view: { name: "home" },
            breadcrumbs: [{ label: "Dashboard", view: { name: "home" } }],
        });
    },

    setOpenNode: (n) => set({ openNode: n }),

    setFilter: (k, v) =>
        set((s) => ({ filters: { ...s.filters, [k]: v } })),

    resetFilters: () => set({ filters: DEFAULT_FILTERS }),

    setToggle: (k, v) =>
        set((s) => ({ toggles: { ...s.toggles, [k]: v } })),
}));
